import { TenantRole, TenantUserSettings, User } from '@/api/generatedApi';
import { api } from '@/services/api';
import { getTenantShortNameFromUrl } from '@/services/tenants/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getErrorStatus } from '@/utils/queryClient';

export const GET_CURRENT_USER_QUERY_KEY = 'users.getCurrentUser';

type RetryPolicy = boolean | ((failureCount: number, error: unknown) => boolean);

const SESSION_REJECTED_STATUS_CODES = new Set([401, 403]);

// Every consumer of this query reads permissions from it, and the query client turns
// off refetch on mount, focus and reconnect, so giving up is permanent for the life of
// the page. A rejected session is the only failure worth giving up on; a 5xx during a
// rolling deploy keeps retrying, exactly as it did before.
const retryUnlessSessionRejected: RetryPolicy = (_failureCount, error) => {
  const status = getErrorStatus(error);
  return !(status && SESSION_REJECTED_STATUS_CODES.has(status));
};

export const useGetCurrentUser = ({
  skipRedirect = false,
  retry = retryUnlessSessionRejected,
}: {
  skipRedirect?: boolean;
  retry?: RetryPolicy;
} = {}) => {
  const location = useLocation();
  const currentTenantShortName = getTenantShortNameFromUrl(location.pathname);

  const { data, isLoading, error } = useQuery({
    queryKey: [GET_CURRENT_USER_QUERY_KEY],
    queryFn: api.users.getCurrentUser,
    select: data => data.data,
    meta: {
      skipRedirect,
    },
    retry,
  });

  const currentTenantSettings: TenantUserSettings | undefined = data?.user?.settings?.find(
    tenantSettings => tenantSettings.tenantShortName === currentTenantShortName
  );

  const requireRoles = (roles: TenantRole[]): boolean => {
    if (!currentTenantSettings) {
      return false;
    }
    const { tenantRole, userEnabled } = currentTenantSettings;
    return Boolean(tenantRole && roles.includes(tenantRole) && userEnabled);
  };

  const onlyRole = (role: TenantRole) => {
    if (!currentTenantSettings) {
      return false;
    }
    return currentTenantSettings.tenantRole === role && Boolean(currentTenantSettings.userEnabled);
  };

  const canOnlySearch: boolean = onlyRole(TenantRole.Search);
  const canManageUsers: boolean = requireRoles([TenantRole.Admin]);
  const canUploadAssets: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canDeleteAssets: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canReprocessAssets: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canModifyAssetMetadata: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canAddMetadataFields: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canCreateFolders: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canDeleteFolders: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canManageMetadataFields: boolean = requireRoles([TenantRole.Admin]);
  const canManageReportGroups: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canManageTaxonomy: boolean = requireRoles([TenantRole.Admin]);
  const canViewTenantActivity: boolean = requireRoles([TenantRole.Admin]);
  const canSetAssetLabels: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const canEditPartFinderReport: boolean = requireRoles([TenantRole.Admin, TenantRole.Author]);
  const isAdmin: boolean = requireRoles([TenantRole.Admin]);
  const isAuthor: boolean = requireRoles([TenantRole.Author]);
  const isViewer: boolean = requireRoles([TenantRole.Viewer]);
  const isSearch: boolean = requireRoles([TenantRole.Search]);

  const tenantRoleLabel = useMemo((): string => {
    if (isAdmin) return 'Admin';
    if (isAuthor) return 'Author';
    if (isViewer) return 'Viewer';
    if (isSearch) return 'Search';
    return 'User';
  }, [isAdmin, isAuthor, isViewer, isSearch]);

  const isUserCurrentUser = (user: User) => {
    return user.email === data?.user.displayName;
  };

  const selfAwareEmail = (email: string) => {
    return `${email === data?.user.displayName ? 'Me' : email}`;
  };

  const currentUserIsAtleastTenantViewer = (tenantId?: string): boolean => {
    if (!tenantId) {
      return false;
    }

    const userTenantSettings = data?.user?.settings?.find(tenant => tenant.tenantId === tenantId && tenant.userEnabled);

    if (!userTenantSettings?.tenantRole) {
      return false;
    }

    return [TenantRole.Viewer, TenantRole.Author, TenantRole.Admin].includes(userTenantSettings.tenantRole);
  };

  // A tenant whose assets live in another environment and are searched over the network. Surfaces
  // that are out of scope over the network (compare) are hidden for its assets. The flag is derived
  // server-side from REMOTE_SEARCH_HOST_MAP — never inferred here from role or tenant name, since a
  // SEARCH-role tenant can be perfectly local.
  const isRemoteSearchTenant = (tenantId?: string): boolean => {
    if (!tenantId) {
      return false;
    }

    return Boolean(data?.user?.settings?.find(tenant => tenant.tenantId === tenantId)?.isRemoteSearchTenant);
  };

  const enabledTenants = (data?.user?.settings || []).filter(({ userEnabled }) => userEnabled);

  return {
    data,
    isLoading,
    error,
    isUserCurrentUser,
    tenants: enabledTenants,
    tenantRoleLabel,
    currentTenant: currentTenantSettings,
    currentUserIsAtleastTenantViewer,
    isRemoteSearchTenant,
    selfAwareEmail,
    permissions: {
      canOnlySearch,
      canManageUsers,
      canUploadAssets,
      canDeleteAssets,
      canReprocessAssets,
      canModifyAssetMetadata,
      canAddMetadataFields,
      canCreateFolders,
      canDeleteFolders,
      canManageMetadataFields,
      canManageReportGroups,
      canManageTaxonomy,
      canViewTenantActivity,
      canSetAssetLabels,
      canEditPartFinderReport,
    },
  };
};
